import type { OnboardedData } from '../api/getComponentList';

export type ComponentKind = 'frontend' | 'backend';
export type ComponentFilter = 'all' | ComponentKind;

export function getComponentDisplayName(repo: OnboardedData): string {
  return repo?.isView ? repo.displayName : repo.repoName;
}

export function getComponentKind(repo: OnboardedData): ComponentKind {
  if (typeof repo.isFrontend === 'boolean') {
    return repo.isFrontend ? 'frontend' : 'backend';
  }

  const hasFrontend = Boolean(repo.frontendUrl?.trim());
  const hasBackend = Boolean(repo.backendUrl?.trim());

  if (hasFrontend && !hasBackend) return 'frontend';
  if (hasBackend && !hasFrontend) return 'backend';

  const primaryTech = repo.detectedTechnologies?.[0];
  if (primaryTech === 'C#') return 'backend';

  return 'frontend';
}

export function getComponentKindLabel(repo: OnboardedData): string {
  return getComponentKind(repo) === 'frontend' ? 'Frontend' : 'Backend';
}

export function formatCompactNumber(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0';
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return String(Math.round(value));
}

/** Parse API scan timestamps. Backend sends UTC naive ISO (no Z); treat as UTC. */
function parseUtcTimestamp(value: string): Date {
  const raw = value.trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  return new Date(hasTimezone ? raw : `${raw}Z`);
}

export function formatScanAge(lastScanAt?: string): string {
  if (!lastScanAt?.trim()) return 'Awaiting first scan';

  const scannedAt = parseUtcTimestamp(lastScanAt);
  if (Number.isNaN(scannedAt.getTime())) return 'not scanned yet';

  const diffMs = Date.now() - scannedAt.getTime();
  if (diffMs < 60_000) return 'scanned just now';

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `Last scanned ${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Last scanned ${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `Last scanned ${days}d ago`;
}

export function matchesComponentSearch(
  repo: OnboardedData,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const haystack = [
    getComponentDisplayName(repo),
    repo.repoName,
    repo.projectName,
    repo.branchName,
    repo.environment,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(q);
}

export function getOnboardedTestScriptCount(repo: OnboardedData): number {
  return repo.inventory?.onboardedTestScriptCount ?? 0;
}

export function getTotalTests(repo: OnboardedData): number {
  return (
    (repo.metrics?.testAutomationCount ?? 0) +
    (repo.metrics?.manualTestCount ?? 0)
  );
}

export function getLineCount(repo: OnboardedData): number {
  return (
    repo.inventory?.executableLines ??
    repo.metrics?.totalLines ??
    repo.metrics?.coveredLines ??
    0
  );
}
